import { Select } from '@appkit4/react-components';
import useLocale from '../../../locale/useLocale';
import { ReasonInformation } from 'api/generated/models';
import { Loading } from '@appkit4/react-components/loading';
import {
    DeclarationTypes,
    NewDeclarationFormValues,
} from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { useFormikContext } from 'formik';
import { useAbsenceReasons } from 'contexts/AbsenceReasonsProvider/AbsenceReasonsProvider';
import { SelectValue } from '@appkit4/react-components/cjs/select/Select';
import { useCallback, useRef } from 'react';
import useFocusOnRender from 'helpers/useFocusOnRender';

export type SubmissionTypeChoiceStepOneProps = {
    setDeclarationType: (declarationType: DeclarationTypes) => void;
};

const SubmissionTypeChoiceStepOne = ({
    setDeclarationType,
}: SubmissionTypeChoiceStepOneProps) => {
    const { l } = useLocale();
    const { values, setFieldValue } =
        useFormikContext<NewDeclarationFormValues>();
    const { absenceReasons, absenceReasonsIsPending } = useAbsenceReasons();
    const inputRef = useRef<HTMLInputElement>(null);
    useFocusOnRender(inputRef);

    const handleTypeChoice = useCallback(
        (selectedValue: SelectValue) => {
            setDeclarationType(selectedValue as DeclarationTypes);
            setFieldValue('declarationType', selectedValue);
            if (selectedValue === DeclarationTypes.Planned) {
                setFieldValue('dateRanges', [
                    {
                        declarationId: undefined,
                        startDate: null,
                        endDate: null,
                    },
                ]);
            } else if (selectedValue === DeclarationTypes.Unforeseen) {
                setFieldValue('unforeseenAbsences', [
                    {
                        declarationId: undefined,
                        startDate: null,
                        reasonForAbsence: '',
                    },
                ]);
            }
        },
        [setFieldValue, setDeclarationType]
    );

    return (
        <>
            {absenceReasonsIsPending ? (
                <Loading
                    loadingType="circular"
                    indeterminate
                    compact={false}
                    className="mt-2"
                />
            ) : (
                <>
                    <div className="padding-10-top-bottom">
                        {l('_pleaseDeclareText')}
                    </div>
                    <div className="row-circumstances">
                        {Array.isArray(absenceReasons) &&
                            absenceReasons?.map((absenceReason) => (
                                <div
                                    className="column-circumstances"
                                    key={absenceReason.declarationType}
                                >
                                    <div
                                        className="ap-typography-heading-s padding-10-top-bottom"
                                        style={{ paddingLeft: '10px' }}
                                    >
                                        {absenceReason.declarationType ===
                                        'Unforeseen Circumstances'
                                            ? l(
                                                  '_unforeseenCircumstancesLowerC'
                                              )
                                            : l('_plannedCircumstancesLowerC')}
                                    </div>
                                    <ul>
                                        {absenceReason?.absenceInformation?.map(
                                            (info: ReasonInformation) => (
                                                <li key={info.reason}>
                                                    {info.reason}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            ))}
                    </div>
                    <h2 className="ap-typography-heading-s">
                        Choose type of circumstance
                    </h2>
                    <Select
                        inputRef={inputRef}
                        style={{ width: '40%' }}
                        data={Object.values(DeclarationTypes)}
                        placeholder={'Please select'}
                        value={values.declarationType}
                        onSelect={(selectedValue) => {
                            handleTypeChoice(selectedValue as DeclarationTypes);
                        }}
                        defaultValue={
                            values.declarationType !== undefined
                                ? values.declarationType
                                : ''
                        }
                    />
                </>
            )}
        </>
    );
};

export default SubmissionTypeChoiceStepOne;
